import { UI } from "../constants/tokens";
import { CURRENCIES } from "../constants/data";
import { getFx } from "./fxRates";

// A dollar amount in the user's currency. No live rate for that currency →
// "—", never a number from a fixed table. The caller re-renders on rate
// changes through useFxRates() higher up (App holds one subscription).
// `whole: true` drops the cents · the format of a portfolio total ($42,157),
// one format wherever the total is the figure a line is about (2026-09-26).
export const fmt = (v, cur = "USD", { whole = false } = {}) => {
  const c  = CURRENCIES[cur] || CURRENCIES.USD;
  const fx = getFx(cur);
  if (!fx) return "—";
  const x = v * fx.rate;
  return cur === "ILS" || whole
    ? c.symbol + x.toLocaleString("en-US", { maximumFractionDigits: 0 })
    : c.symbol + x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const pct = (a, b) => (((b - a) / a) * 100).toFixed(2);

// ─── Percent, one rule (sprint 17) ───────────────────────────────────────────
// A change · daily, since purchase, an index · to two places; a share of the
// portfolio · weight, exposure · to one. The sign is the real minus "−"
// (U+2212), never the hyphen toFixed() puts on a negative number, and a figure
// that rounds to zero carries none: "0.00%", not "−0.00%".
export const MINUS = "\u2212";
export const PCT_CHANGE = 2;
export const PCT_SHARE = 1;
export function fmtPct(v, { decimals = PCT_CHANGE, signed = true } = {}) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const s = Math.abs(n).toFixed(decimals);
  const zero = !/[1-9]/.test(s);
  const sign = zero ? "" : n < 0 ? MINUS : signed ? "+" : "";
  return `${sign}${s}%`;
}
// The direction of a figure as it will be shown: +1, −1, or 0 when it rounds
// to zero at that many places · what colour and sign are read from.
// A dollar figure as it will be drawn in the display currency: converted at
// the live rate and rounded the way Amount rounds it (shekels whole, others
// to the cent). Its colour is read from this, so "₪0" is never green.
export function shownMoneyDir(usd, cur = "USD", decimals = null) {
  const fx = getFx(cur);
  if (!fx || !Number.isFinite(Number(usd))) return 0;
  return shownDir(Number(usd) * fx.rate, decimals ?? (cur === "ILS" ? 0 : 2));
}
export function shownDir(v, decimals = PCT_CHANGE) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return /[1-9]/.test(Math.abs(n).toFixed(decimals)) ? Math.sign(n) : 0;
}

export function peColor(pe) {
  // A P/E is a valuation, not a verdict — no colour says "cheap" here.
  if (!pe) return UI.text.muted;
  return UI.text.secondary;
}

// Remove stray "[n]" citation markers that leak in from source text, plus the
// extra whitespace / space-before-punctuation they leave behind. Legitimate
// content (e.g. "$21", "21%") is untouched — only bracketed digits are stripped.
// Applied at display time so existing cached summaries are cleaned too.
export function stripRefMarkers(text) {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(/\s*\[\d+\]/g, "")
    .replace(/\s+([.,;:!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();
}
